/**
 * LayoutToggle — list ↔ Kanban layout switch for the trial board.
 *
 * A controlled two-position control mirroring {@link FidelityRocker}'s tablist
 * pattern and styling, so the toolbar reads as one coherent set of view
 * controls. Purely presentational: props in, JSX out. No fetch, no polling, no
 * global reads — the parent owns the layout state (session-persisted via
 * `board-layout.ts`) and passes it down.
 *
 *   list   = the dense, recency-sorted activity stream
 *   kanban = the same trials grouped into the five status lanes
 */

import type { BoardLayout } from './board-layout'

export interface LayoutToggleProps {
  value: BoardLayout
  onChange: (next: BoardLayout) => void
}

/** ARIA Authoring Practices §Tabs pattern: arrow-key navigation between tabs. */
function handleLayoutKeyDown(e: React.KeyboardEvent<HTMLDivElement>, onChange: (next: BoardLayout) => void): void {
  if (e.key === 'ArrowRight') {
    e.preventDefault()
    onChange('kanban')
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault()
    onChange('list')
  }
}

export function LayoutToggle({ value, onChange }: LayoutToggleProps): JSX.Element {
  return (
    <div
      className="layout-toggle"
      role="tablist"
      aria-label="Board layout"
      onKeyDown={(e) => handleLayoutKeyDown(e, onChange)}
    >
      <button
        type="button"
        role="tab"
        className={`layout-toggle-tab${value === 'list' ? ' is-active' : ''}`}
        aria-selected={value === 'list'}
        tabIndex={value === 'list' ? 0 : -1}
        data-testid="layout-list"
        onClick={() => onChange('list')}
      >
        list
      </button>
      <button
        type="button"
        role="tab"
        className={`layout-toggle-tab${value === 'kanban' ? ' is-active' : ''}`}
        aria-selected={value === 'kanban'}
        tabIndex={value === 'kanban' ? 0 : -1}
        data-testid="layout-kanban"
        onClick={() => onChange('kanban')}
      >
        kanban
      </button>
    </div>
  )
}
